/**
 * screens/modals/ReverseModal
 * ──────────────────────────────────────────────────────────────────────────
 * Correct a ledger row.
 *
 * This does not edit anything. It APPENDS a reversing entry that points at the
 * original, and the original stays exactly where it is — that is invariant 1,
 * and it is why the ledger can be audited at all. The modal says so plainly,
 * because someone reaching for "Reverse" is usually looking for an edit button
 * and needs to know what they are about to do instead.
 *
 * A row can be reversed once. The drawer hides the action on a row that already
 * has a reversal, and a unique index in the database enforces it — so a
 * double-clicked Reverse produces one reversal, not two.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { Modal, Textarea } from '../../components';
import { reverseTransaction } from '../../services/cash/cash.service';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface ReverseModalProps {
  txnId: string;
}

export function ReverseModal({ txnId }: ReverseModalProps) {
  const { closeModal } = useShell();
  const [memo, setMemo] = useState('');

  const { submitting, run } = useModalSubmit({
    submit: () => reverseTransaction(txnId, memo.trim() || undefined),
    title: 'Reversal posted',
    fallback: 'A reversing entry has been appended. The original row is unchanged.',
  });

  return (
    <Modal
      visible
      title="Reverse this transaction"
      hint="The ledger is append-only. This adds a reversing entry — the original row stays."
      onClose={closeModal}
      primaryLabel="Post the reversal"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Textarea
        label="Why"
        hint="Optional, but it is what the next person reading this ledger will see."
        value={memo}
        onChangeText={setMemo}
        placeholder="Duplicate entry"
        maxLength={240}
        rows={3}
      />
    </Modal>
  );
}

export default ReverseModal;
